import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import ExamViewer from '../../../components/ExamViewer';
import {
  ExamAttempt,
  Section,
} from '../../../components/ExamViewer/types';
import { NoPermissionMessage } from '../../../components/PageLayout';
import SkeletonStack from '../../../components/SkeletonStack';
import {
  useGetExamAttemptQuery, useGetExamQuery,
} from '../../../services/exams';
import { Question } from '../../../services/exams/types';
import { useGetStudentQuery } from '../../../services/students';
import { QuestionType } from '../../../types/common';
import { getQuestionSections } from '../../../utils/common/exams';

import {
  getExamMCQMarks,
  questionAttemptsArrayToExamAttempt,
} from './utils';

interface MarkingPageProps {
  subject: string,
  studentId: string,
  examId: string,
  attemptNum: number
}

const IndividualExam = ({ subject, studentId, examId, attemptNum }: MarkingPageProps) => {
  const navigate = useNavigate();

  const [loadExamAttempt, setLoadExamAttempt] = useState(true);

  const { data: exam, isSuccess: isExamLoaded, isLoading: isExamLoading } =
    useGetExamQuery({ subject, examId });
  const {
    data: examAttempt,
    isLoading: isExamAttemptLoading,
    isSuccess: isExamAttemptLoaded,
    isError: isExamAttemptError,
  } = useGetExamAttemptQuery({
    subject, examId, studentId, attemptNum,
  }, { skip: !loadExamAttempt });

  const { data: student, isLoading: isStudentLoading } = useGetStudentQuery({ studentId });

  const [attempts, setAttempts] = useState<ExamAttempt>({});
  const [questionSectionIndex, setQuestionSectionIndex] = useState(0);
  const [questionSections, setQuestionSections]
   = useState<Section[]>([]);
  const [isMCQ, setIsMCQ] = useState(false);

  const [displayedQuestions, setDisplayedQuestions] = useState<Question[]>([]);

  const [MCQMarks, setMCQMarks] = useState({
    correct: 0,
    total: 0,
  });

  useEffect(() => {
    // Stop trying to fetch exam attempt once backend returns the previous attempt
    // data, or if there is no attempt
    if (isExamAttemptLoaded || isExamAttemptError) setLoadExamAttempt(false);
  }, [isExamAttemptLoaded, isExamAttemptError]);

  useEffect(() => {
    if (examAttempt) {
      // Convert an array of question attempts to an object with questionId as key
      const attemptObject = questionAttemptsArrayToExamAttempt(examAttempt?.data.questions);
      if (attemptObject) setAttempts(attemptObject);
    }
    if (exam) {
      const splitSections = getQuestionSections(exam?.data.questions ?? [], 10);
      setQuestionSections(splitSections);
      setDisplayedQuestions(splitSections[0]?.questions ?? []);
    }
  }, [exam, examAttempt]);

  useEffect(() => {
    if (displayedQuestions[0]?.type === QuestionType.MultipleChoice) {
      setIsMCQ(true);
      // This currently only works if there is one MCQ set, needs updating if not
      setMCQMarks(getExamMCQMarks(displayedQuestions, attempts));
    } else setIsMCQ(false);
  }, [displayedQuestions]);

  useEffect(() => {
    setDisplayedQuestions(questionSections[questionSectionIndex]?.questions ?? []);
  }, [questionSections, questionSectionIndex]);

  const changeQuestionSection = (index: number) => {
    setQuestionSectionIndex(index);
  };

  const handleProgress = async () => {
    if (questionSectionIndex === questionSections.length - 1) {
      // If currently on the last question, progress button should return to exams tab
      navigate(`/analytics/${studentId}`);
    } else {
      changeQuestionSection(questionSectionIndex + 1);
    }
  };

  /**
   * RENDER
   */

  if (isExamLoading || isStudentLoading || isExamAttemptLoading) {
    return (
      <SkeletonStack />
    );
  }

  if (isExamLoaded && !exam) {
    // If the exam is loaded but no result returned, then block access
    return (
      <NoPermissionMessage />
    );
  }

  return (
    <ExamViewer
      heading={`${student?.data.name}'s Exam`}
      subHeading={exam?.data.label}
      questionSections={questionSections}
      questionSectionIndex={questionSectionIndex}
      isMCQ={isMCQ}
      isMarked
      MCQCorrect={MCQMarks.correct}
      MCQTotal={MCQMarks.total}
      displayedQuestions={displayedQuestions}
      attempts={attempts}
      changeQuestionSection={changeQuestionSection}
      handleProgress={handleProgress}
      showAnalytics
      finishButtonText="Return to Exams"
    />
  );
};

export default IndividualExam;
